/* eslint-disable @typescript-eslint/no-explicit-any */
import { ReactElement, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { AdaButton, AdaLink, Icon, Loading, Tooltip } from '@betrybe/ada-react';
import * as Sentry from '@sentry/browser';

import Alert from '@/components/Alert';
import { CheckboxInput } from '@/components/molecules';
import { CardShapedButton } from '@/components/atoms';
import { PersonCard } from '@/components/organisms';
import { StudentContext, useToastNotification } from '@/contexts';
import { useClient, useStateMachine, useLazyTrack } from '@/hooks';
import { useMscPipeline, useGuarantor } from '@/services/pre-enrollment';
import { DefaultLayout } from '@/layouts';
import { ActorTypeEnum, SourceEnum, VerbEnum } from '@/tracker/enums';
import StatesEnum from '@/utils/enums/StatesEnum';
import {
  canChangePaymentMethod,
  canSendToAnalisys,
  shouldHaveGuarantor,
  shouldHaveTwoGuarantor,
} from '@/utils';
import DescriptionText from './components';

function RegisterGuarantors(): ReactElement {
  const { getGuarantors } = useGuarantor();
  const { state, setState } = useContext(StudentContext);
  const {
    currentState,
    stateMachineVersion,
    clientId,
    processInfo,
    mscPipelineId,
    selectionProcessId,
    processDateInfo,
    setScoreDisapprovedGuarantorId,
    setActualState,
  } = useClient();
  const [authorize, setAuthorize] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [hasAuthorizeError, setAuthorizeError] = useState<boolean>(false);
  const history = useHistory();
  const [, toastNotificationDispatch] = useToastNotification();
  const track = useLazyTrack();
  const { changeMscPipeline } = useMscPipeline();
  const { getStateObject } = useStateMachine();
  const isV3 = stateMachineVersion === 'V3';

  const hasGuarantor =
    state.guarantorsPerson.length < 1 &&
    shouldHaveGuarantor.indexOf(currentState as StatesEnum) >= 0;
  const hasTwoGuarantors =
    state.guarantorsPerson.length < 2 &&
    shouldHaveTwoGuarantor.indexOf(currentState as StatesEnum) >= 0;

  useEffect(() => {
    if (
      isV3 &&
      currentState === StatesEnum.SCORE_DISAPPROVED &&
      window.location.pathname === '/register/guarantors'
    ) {
      history.push('/register/disapproval');
      setLoading(false);
    } else if (hasGuarantor || hasTwoGuarantors) {
      setLoading(true);
      getGuarantors(clientId)
        .then(async (res) => {
          await setState({
            ...state,
            guarantorsPerson: res.data,
          });

          if (
            currentState &&
            (currentState as StatesEnum) === StatesEnum.SCORE_DISAPPROVED &&
            res.data[0]
          ) {
            setScoreDisapprovedGuarantorId(res.data[0].id);
          }
        })
        .catch((error) => {
          const payload = {
            userId: clientId,
            currentState,
          };
          Sentry.setUser({ id: clientId });
          Sentry.captureException({ error, payload });
        })
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const callTrack = (event: string, component: string) => {
    track(
      event,
      {
        actor: {
          user_id: clientId,
          type: ActorTypeEnum.CANDIDATE,
          role: '',
        },
        object: {
          path: window.location.pathname,
          url: window.location.href,
          component,
          process_info: processInfo,
        },
        context: {
          source: SourceEnum.FINANCING_REQUEST,
        },
        verb: VerbEnum.CLICK,
      },
      'track',
    );
  };

  const handleClick = async () => {
    callTrack('Financing Request Completed', 'Enviar para análise de crédito');

    const enrollmentHasValidState =
      canSendToAnalisys.indexOf(currentState as StatesEnum) !== -1;

    if (enrollmentHasValidState && !authorize) {
      setAuthorizeError(true);
    } else {
      try {
        await changeMscPipeline({
          mscPipelineId,
          selectionProcessId,
          event: StatesEnum.FINISH_FINANCING_REQUEST,
          creditCheckAuth: authorize,
        });

        const stateObj = getStateObject({ state: currentState });

        if (stateObj && stateObj.nextState) {
          await setActualState(stateObj.nextState);
          history?.push(stateObj.nextUrl);
        }
      } catch (err: unknown) {
        const payload = {
          userId: clientId,
          currentState,
        };
        Sentry.setUser({ id: clientId });
        Sentry.captureException({ err, payload });

        toastNotificationDispatch({
          type: 'close',
          payload: { text: '', title: '' },
        });

        toastNotificationDispatch({
          type: 'error',
          payload: {
            title: 'Falha ao enviar para análise de crédito',
            text: 'Falha ao enviar para análise de crédito',
          },
        });
      }
    }
  };

  const handleClickChangePayment = async () => {
    try {
      await changeMscPipeline({
        mscPipelineId,
        selectionProcessId,
        event: StatesEnum.CHANGE_PAYMENT_METHOD,
      });
      setState({
        ...state,
        step: StatesEnum.CHANGE_PAYMENT_METHOD,
      });
      history.push('/redirect-education');
      callTrack(
        'Payment Method Change Intended',
        'Alterar forma de pagamento.',
      );
    } catch (err: any) {
      const payload = {
        userId: clientId,
        currentState,
      };
      Sentry.setUser({ id: clientId });
      Sentry.captureException({ err, payload });

      toastNotificationDispatch({
        type: 'close',
        payload: { text: '', title: '' },
      });

      toastNotificationDispatch({
        type: 'error',
        payload: {
          title: 'Falha ao alterar forma de pagamento',
          text: err,
        },
      });
    }
  };

  const textTitle = () => {
    if (isV3 || state.paymentMethod === 'fifteen_delay') {
      if (currentState === StatesEnum.SCORE_DISAPPROVED) {
        return <>Adicione a segunda pessoa avalista</>;
      }
      if (currentState === StatesEnum.CREDIT_DISAPPROVED) {
        return <>Adicione outras pessoas avalistas</>;
      }
      return <>Revise os dados e confirme o envio</>;
    }
    return <>Cadastre suas pessoas avalistas</>;
  };

  const isReproved =
    currentState === StatesEnum.DISAPPROVED ||
    currentState === StatesEnum.CREDIT_DISAPPROVED;
  const isButtonDisabled =
    (isV3 || state.paymentMethod === 'fifteen_delay') &&
    canSendToAnalisys.indexOf(currentState as StatesEnum) < 0;

  if (loading)
    return (
      <DefaultLayout cardSize="normal-card">
        <Loading> Carregando... </Loading>
      </DefaultLayout>
    );

  return (
    <DefaultLayout
      title={textTitle()}
      titleAlign="left"
      cardSize="normal-card"
      step="2 de 4"
      description={
        <>
          <div className="register-gurantors-container__desciption-element">
            <div className="register-gurantors-container__desciption-element--large-card">
              <DescriptionText
                isV3={isV3}
                currentState={currentState}
                paymentMethod={state.paymentMethod}
              />
            </div>
          </div>
          <Alert />
        </>
      }
      progressPercentage={50}
      limitDate={
        processDateInfo?.limitDateFinancingRequest ||
        state.processDateInfo?.limitDateFinancingRequest
      }
    >
      <div className="register-gurantors-container">
        <div className="register-gurantors-container__cards-div">
          {canChangePaymentMethod.indexOf(currentState as StatesEnum) >= 0 && (
            <CardShapedButton />
          )}
          {currentState === StatesEnum.SCORE_REGISTRATION_COMPLETED && (
            <PersonCard
              name={state.clientData.name}
              person="candidate"
              maritalStatus={state.clientData.marital_status}
              cpf={state.clientData.cpf}
              rg={state.clientData.rg}
              address={state.clientData.address}
              residenceNumber={state.clientData.residence_number}
              neighborhood={state.clientData.neighborhood}
              id="candidate-card"
              isReproved={isReproved}
            />
          )}
          {state.guarantorsPerson?.map((guarantor, index) => {
            const key = index;
            return (
              <PersonCard
                name={guarantor.name}
                person="guarantor"
                maritalStatus={guarantor.marital_status}
                kinship={guarantor.degree_of_relatedness}
                phone={guarantor.phone_number}
                email={guarantor.email}
                cpf={guarantor.cpf}
                rg={guarantor.rg}
                address={guarantor.address}
                residenceNumber={guarantor.residence_number}
                neighborhood={guarantor.neighborhood}
                id={`guarantor-card-${key}`}
                isReproved={isReproved}
                key={key}
                guarantorId={guarantor.id}
              />
            );
          })}
        </div>
        {!isButtonDisabled && (
          <CheckboxInput
            id="1"
            label={
              <>
                Autorizo usar minhas informações e da(s) pessoa(s) avalista(s)
                para análise de crédito. Também autorizo usar minhas informações
                para consulta ao Sistema de Informações de Créditos (SCR) do
                Banco Central do Brasil.
                <Tooltip
                  backgroundColor="black-800"
                  title="O Sistema de Informações de Créditos (SCR) é um banco de dados que mostra quais empréstimos, financiamentos e garantias você possui com instituições autorizadas a funcionar pelo Banco Central. "
                >
                  <Icon
                    name="help"
                    className="register-container__tooltip-icon"
                    width={18}
                  />
                </Tooltip>
              </>
            }
            name="authorize"
            isControlled={false}
            disabled={false}
            checked={authorize}
            onChange={() => {
              setAuthorize(!authorize);
              setAuthorizeError(authorize);
              callTrack(
                'Personal Information Usage Authorized',
                'Eu autorizo o uso das minhas informações e das pessoas avalistas para análise de crédito.Também autorizo o uso das minhas informações para consulta ao Sistema de Informações de Créditos (SCR) do Banco Central do Brasil.',
              );
            }}
            value="true"
            data-testid="authorize"
          />
        )}
        {hasAuthorizeError && (
          <div className="flex flex-row items-center text-xs text-coral-700 mt-5">
            <Icon name="error_outline" height={16} />
            <p>
              É necessário autorizar o uso das informações para prosseguir. Para
              prosseguir, marque a caixa de seleção de autorização.
            </p>
          </div>
        )}
        <div className="register-gurantors-container__button-div">
          {canChangePaymentMethod.indexOf(currentState as StatesEnum) >= 0 && (
            <div className="register-gurantors-container__button-div--change-payment-container">
              <p>Deseja alterar a forma de pagamento?</p>
              <AdaLink onClick={handleClickChangePayment} color="#4DA6FF">
                Clique aqui
              </AdaLink>
            </div>
          )}
          <AdaButton
            onClick={() => {
              handleClick();
            }}
            disabled={isButtonDisabled}
            data-testid="submit-button"
          >
            Enviar para análise de crédito
          </AdaButton>
        </div>
      </div>
    </DefaultLayout>
  );
}

export default RegisterGuarantors;
